import { useContext } from 'react';
import { TableFilterContext, filterWords, textMatches } from './tableFilter';
import type { BoardroomAudience, BoardroomAudiencePerson, BoardroomAudienceSpot } from '../../../types';

/**
 * Where this market talks, ranked for joining in.
 *
 * Drawn from the posts the room's searches already brought back
 * (`boardroom/audience.py`): nothing is searched for it. Room 54 had 5,500 such
 * posts across hundreds of groups, subreddits and channels, so each part shows
 * the best few, with the total beside it.
 *
 * Places to join in first; channels and review sites, which are someone else's
 * own content, under "Worth watching"; then the people who post most.
 */

const fmt = (n: number) => n.toLocaleString();

function plural(n: number, one: string, many = `${one}s`) {
  return `${fmt(n)} ${n === 1 ? one : many}`;
}

/** What the room saw there, in one line: size, posts on the subject, talk, quotes. */
function seenLine(s: BoardroomAudienceSpot): string {
  return [
    s.members ? plural(s.members, 'member') : '',
    s.activity,
    s.posts ? `${plural(s.posts, 'post')} on your subject${s.recent ? ` (${fmt(s.recent)} recent)` : ''}` : '',
    s.replies ? plural(s.replies, 'reply', 'replies') : '',
    s.quoted ? `${plural(s.quoted, 'complaint')} quoted` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

function Ext({ href, children, className = '' }: { href: string; children: React.ReactNode; className?: string }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className={`hover:underline ${className}`}>
      {children}
    </a>
  );
}

function SpotRow({ s }: { s: BoardroomAudienceSpot }) {
  const seen = seenLine(s);
  return (
    <li className="flex items-start gap-3 py-2" data-testid="audience-spot">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <Ext href={s.url} className="font-medium text-gray-900 dark:text-gray-100">
            {s.name}
          </Ext>
          <span className="text-[11px] text-gray-500 dark:text-gray-400">
            {s.kind}
            {s.privacy === 'private' ? ' · private' : ''}
            {s.vendor ? ' · a company on your board' : ''}
          </span>
        </div>
        {seen && <div className="text-xs text-gray-600 dark:text-gray-300">{seen}</div>}
        {(s.names.length > 0 || s.best) && (
          <div className="truncate text-xs text-gray-500 dark:text-gray-400">
            {s.names.length > 0 && <>Talks about {s.names.join(', ')}</>}
            {s.names.length > 0 && s.best && ' · '}
            {s.best && (
              <Ext href={s.best.url} className="text-indigo-600 dark:text-indigo-400">
                {s.best.replies ? `Busiest post (${plural(s.best.replies, 'reply', 'replies')})` : 'A post'}: {s.best.text}
              </Ext>
            )}
          </div>
        )}
      </div>
      {(s.how || s.status) && (
        <div className="shrink-0 text-right text-xs">
          {s.how && <div className="text-gray-700 dark:text-gray-200">{s.how}</div>}
          {s.status && <div className="text-emerald-700 dark:text-emerald-400">{s.status}</div>}
        </div>
      )}
    </li>
  );
}

function PersonRow({ p }: { p: BoardroomAudiencePerson }) {
  return (
    <li className="flex items-start gap-3 py-2" data-testid="audience-person">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium text-gray-900 dark:text-gray-100">{p.name}</span>
          <span className="text-[11px] text-gray-500 dark:text-gray-400">
            {p.site}
            {p.vendor ? ' · a company on your board' : ''}
          </span>
        </div>
        <div className="text-xs text-gray-600 dark:text-gray-300">
          {plural(p.posts, 'post')} on your subject{p.recent ? ` (${fmt(p.recent)} recent)` : ''}
          {p.places.length > 0 && <> · in {p.places.join(', ')}</>}
          {p.names.length > 0 && <> · about {p.names.join(', ')}</>}
        </div>
        {p.latest && (
          <div className="truncate text-xs">
            <Ext href={p.latest.url} className="text-indigo-600 dark:text-indigo-400">
              Latest: {p.latest.text}
            </Ext>
          </div>
        )}
      </div>
    </li>
  );
}

function Section({
  title,
  hint,
  shown,
  total,
  children,
}: {
  title: string;
  hint: string;
  shown: number;
  total: number;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-5">
      <div className="flex items-baseline gap-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {shown < total ? `best ${shown} of ${fmt(total)}` : fmt(total)}
        </span>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      <ul className="divide-y divide-gray-100 dark:divide-gray-800">{children}</ul>
    </section>
  );
}

export default function AudienceReport({ data, limit }: { data: BoardroomAudience; limit?: number }) {
  // THE ROOM'S FILTER, read here since this table is not a `DataTable`.
  const words = filterWords(useContext(TableFilterContext));
  const spotMatches = (s: BoardroomAudienceSpot) =>
    textMatches(`${s.name} ${s.kind} ${s.names.join(' ')}`, words);
  const personMatches = (p: BoardroomAudiencePerson) =>
    textMatches(`${p.name} ${p.site} ${p.places.join(' ')} ${p.names.join(' ')}`, words);
  const take = data.take_part.filter(spotMatches);
  const listen = data.listen.filter(spotMatches);
  const people = data.people.filter(personMatches);

  // A CARD on the board: the best few places to join in, nothing else.
  if (limit) {
    return take.length ? (
      <ul className="divide-y divide-gray-100 dark:divide-gray-800">
        {take.slice(0, limit).map(s => (
          <SpotRow key={s.url} s={s} />
        ))}
      </ul>
    ) : (
      <div className="text-xs text-gray-500 dark:text-gray-400">No places found yet.</div>
    );
  }
  if (!data.totals.take_part && !data.totals.listen && !data.totals.people) {
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400">
        Nothing yet. This fills in from the room's social searches: each group, subreddit or forum their posts came from.
      </div>
    );
  }
  return (
    <div data-testid="audience">
      {take.length > 0 && (
        <Section
          title="Where to join in"
          hint="Groups, subreddits and forums where this market posts. Ranked by how much they talk about what's on your board, how recently, and how many replies posts get."
          shown={take.length}
          total={words.length ? take.length : data.totals.take_part}
        >
          {take.map(s => (
            <SpotRow key={s.url} s={s} />
          ))}
        </Section>
      )}
      {listen.length > 0 && (
        <Section
          title="Worth watching"
          hint="Channels, publications and review sites: other people's content, where buyers learn and compare."
          shown={listen.length}
          total={words.length ? listen.length : data.totals.listen}
        >
          {listen.map(s => (
            <SpotRow key={s.url} s={s} />
          ))}
        </Section>
      )}
      {people.length > 0 && (
        <Section
          title="People who post about it"
          hint="Who posts most on this subject, and where."
          shown={people.length}
          total={words.length ? people.length : data.totals.people}
        >
          {people.map(p => (
            <PersonRow key={`${p.site}:${p.name}`} p={p} />
          ))}
        </Section>
      )}
    </div>
  );
}
